import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SmsIcon from '@mui/icons-material/Sms';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { func, object, string } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Http from 'services/Http';
import authCompanyId from 'store/selectors/authCompanyId';
import {
  useAuthorizeSessionMutation,
  useCheckSessionStatusMutation,
  useRequestOTPMutation,
  useVerifyOTPMutation,
} from 'store/app/entities/OmmnioSignSlice';
import { I18n } from 'utils/i18n';
import {
  getAndroidAppHashFromBridge,
  isReactNativeWebView,
  isPlatformAuthenticatorAvailable,
  isWebAuthnSupported,
  preparePublicKeyCredential,
  preparePublicKeyOptions,
  prepareProxyAssertion,
  proxyWebAuthnCreate,
} from 'utils/webAuthn';
import { getEmbedParentTargetOrigin, isTrustedWebOrigin } from 'utils/webOrigin';
import { getWebViewSession } from 'utils/webViewSession';
import { normalizeOmmnioSignError } from '../error-utils';
import { sessionReuseContinueAction, shouldAutoReuseOtpSigningSession } from '../otpSessionReuse';
import { isPasskeyPromptCancelled, passkeyChallengeAction } from '../passkeyChallengeAction';
import { extractSignatureFromWidgets } from '../widget-utils';
import { ommnioSignSafeAreaPadding } from '../safeAreaStyles';

const rootSx = {
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

const contentSx = {
  flexGrow: 1,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxSizing: 'border-box',
  px: 2,
};

const actionsSx = theme => ({
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'space-between',
  alignItems: 'center',
  ...ommnioSignSafeAreaPadding,
  [theme.breakpoints.down('sm')]: {
    paddingTop: 0,
  },
});

const otpInputSx = {
  mt: 2,
  mb: 2,
};

const otpInputProps = {
  maxLength: 6,
  autoComplete: 'one-time-code',
  inputMode: 'numeric',
};

const AuthenticationStep = ({
  signerToken,
  widgets = {},
  deliveryChannel,
  signerPhone,
  companyId: signingCompanyId,
  onAuthComplete,
  onBack,
}) => {
  const authEmployee = useSelector(state => state.auth.employee);
  // `state.auth.company_id` is only populated after GET_USER_DATA on this origin (or via
  // redux-persist rehydration). The signing route isn't auth-gated and often loads on the
  // apex host (RN WebView, cross-origin desktop iframe), so this is usually null there.
  const authStateCompanyId = useSelector(authCompanyId);
  const [requestOTP] = useRequestOTPMutation();
  const [verifyOTP] = useVerifyOTPMutation();
  const [authorizeSession] = useAuthorizeSessionMutation();
  const [checkSessionStatus] = useCheckSessionStatusMutation();
  const [, setAuthMethod] = useState(() => (isWebAuthnSupported() ? 'webauthn' : 'otp'));
  const [otpSent, setOtpSent] = useState(false);
  const [sessionProbeComplete, setSessionProbeComplete] = useState(false);
  const [sessionReusable, setSessionReusable] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const [checkingPasskeys, setCheckingPasskeys] = useState(true);
  const webAuthnInProgressRef = useRef(false);
  const userCancelledRef = useRef(false);
  const forceVerifiedPasskeyRef = useRef(false);
  const pendingUpgradeCredentialsRef = useRef([]);
  const otpSentRef = useRef(false);
  const enrollmentStartedRef = useRef(false);
  const handleVerifyOTPRef = useRef(null);
  const handleRegisterPasskeyRef = useRef(null);
  const handleUpgradeCredentialRef = useRef(null);

  // Tenant id for the passkey pre-check (`x-company` header), in order of reliability:
  // 1. The signing-page response: the signature request's own company, backend-verified.
  // 2. Redux auth state (see notes above — usually empty on the apex host).
  // 3. The session injected by the RN DigitalSignatureScreen.
  const companyId =
    signingCompanyId ||
    authEmployee?.company?._id ||
    authEmployee?.company ||
    authStateCompanyId ||
    getWebViewSession()?.companyId;
  const [deviceSupportsWebAuthn, setDeviceSupportsWebAuthn] = useState(false);
  const [offeringRegistration, setOfferingRegistration] = useState(false);
  /** Passkey exists on another device; after dismissing cross-device QR, offer local enrollment. */
  const [offeringLocalPasskeyRegistration, setOfferingLocalPasskeyRegistration] = useState(false);
  const [registering, setRegistering] = useState(false);

  // Credential upgrade state
  const [upgradeAvailable, setUpgradeAvailable] = useState(false);
  const [upgradeCredentials, setUpgradeCredentials] = useState([]);
  const [verifiedPasskeyAvailable, setVerifiedPasskeyAvailable] = useState(false);
  const [smsFallback, setSmsFallback] = useState(false);
  const [upgradingCredential, setUpgradingCredential] = useState(false);

  /** Email-delivered links must use SMS OTP (signer token); WebAuthn targets in-app session + verified passkeys and dead-ends here. */
  const emailChannelUsesOtpOnly = deliveryChannel === 'email';
  const hasPhoneForEmailOtp =
    typeof signerPhone === 'string' ? signerPhone.trim().length > 0 : Boolean(signerPhone);

  // Check device capability and existing passkeys
  useEffect(() => {
    const checkCapabilityAndPasskeys = async () => {
      if (emailChannelUsesOtpOnly) {
        setCheckingPasskeys(false);
        setDeviceSupportsWebAuthn(false);
        setHasPasskeys(false);
        setOfferingRegistration(false);
        setUpgradeAvailable(false);
        setAuthMethod('otp');
        return;
      }

      // First check if WebAuthn API exists (or React Native WebView)
      if (!isWebAuthnSupported()) {
        setCheckingPasskeys(false);
        setDeviceSupportsWebAuthn(false);
        setHasPasskeys(false);
        setAuthMethod('otp');
        return;
      }

      // Check if device has platform authenticator (biometrics available)
      try {
        const available = await isPlatformAuthenticatorAvailable();
        setDeviceSupportsWebAuthn(available);

        if (!available) {
          // Device doesn't support biometrics, use SMS silently
          setCheckingPasskeys(false);
          setHasPasskeys(false);
          setAuthMethod('otp');
          return;
        }

        // Device supports biometrics, check if user has registered passkeys
        if (!companyId) {
          setCheckingPasskeys(false);
          setHasPasskeys(false);
          return;
        }

        const response = await Http.get('/v1/employee/me/webauthncredentials', {
          headers: { 'x-company': companyId },
        });

        const credentials = response.data?.credentials || [];
        const hasKeys = credentials.length > 0;
        setHasPasskeys(hasKeys);

        // If device supports it but user hasn't registered, offer registration
        if (!hasKeys) {
          setOfferingRegistration(true);
        }
      } catch {
        // If capability check fails, assume not available
        setDeviceSupportsWebAuthn(false);
        setHasPasskeys(false);
        setAuthMethod('otp');
      } finally {
        setCheckingPasskeys(false);
      }
    };

    checkCapabilityAndPasskeys();
  }, [companyId, emailChannelUsesOtpOnly]);

  useEffect(() => {
    otpSentRef.current = otpSent;
  }, [otpSent]);

  // Android RN WebView: native SMS Retriever forwards OTP via postMessage
  useEffect(() => {
    if (!isReactNativeWebView()) {
      return undefined;
    }

    const handleNativeOtp = event => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type !== 'OMMNIOSIGN_OTP_AUTO_FILL' || !/^\d{6}$/.test(data.code)) {
          return;
        }

        setOtpCode(data.code);
        if (otpSentRef.current) {
          handleVerifyOTPRef.current?.(data.code);
        }
      } catch {
        // Ignore parse errors from unrelated postMessage payloads
      }
    };

    window.addEventListener('message', handleNativeOtp);
    return () => window.removeEventListener('message', handleNativeOtp);
  }, []);

  const handleWebAuthnAuth = useCallback(async () => {
    // Prevent multiple simultaneous WebAuthn operations
    if (webAuthnInProgressRef.current) {
      return;
    }

    try {
      webAuthnInProgressRef.current = true;
      setLoading(true);
      setError(null);

      // 1. Generate OmmnioSign WebAuthn challenge
      // Backend gets employeeId from authenticated session and derives requestId from signerToken
      const challengeResponse = await Http.post('/v1/ommniosign/webauthn/challenge', {
        signerToken,
      });

      if (!challengeResponse?.data) {
        throw new Error(I18n.t('OmmnioSign.Errors.WebAuthnFailed'));
      }

      const {
        challenge,
        allowCredentials,
        evidenceId,
        timeout,
        userVerification,
        rpId,
        upgradeCredentials: upgradeCreds,
      } = challengeResponse.data;

      if (!challenge || !evidenceId) {
        throw new Error(I18n.t('OmmnioSign.Errors.WebAuthnFailed'));
      }

      const unverifiedPasskeys = Array.isArray(upgradeCreds) ? upgradeCreds : [];
      pendingUpgradeCredentialsRef.current = unverifiedPasskeys;
      const challengeAction = passkeyChallengeAction({
        allowCredentials,
        upgradeCredentials: unverifiedPasskeys,
        forceVerifiedPasskey: forceVerifiedPasskeyRef.current,
      });

      // Settings passkeys need a one-time OTP before they can sign.
      // Mixed accounts (verified passkey on another device + settings passkey here)
      // stop here so iOS is not sent straight to the cross-device QR.
      if (challengeAction === 'upgrade-only' || challengeAction === 'choose') {
        forceVerifiedPasskeyRef.current = false;
        setUpgradeAvailable(true);
        setUpgradeCredentials(unverifiedPasskeys);
        setVerifiedPasskeyAvailable(challengeAction === 'choose');
        setSmsFallback(false);
        setLoading(false);
        webAuthnInProgressRef.current = false;
        return;
      }

      forceVerifiedPasskeyRef.current = false;

      if (challengeAction === 'missing') {
        throw new Error(
          I18n.t('OmmnioSign.Authentication.NoVerifiedPasskeys') ||
            'No verified passkeys available. Please register a passkey.',
        );
      }

      // 3. Request credential from browser
      // If we're in an iframe or React Native WebView, proxy the request to bypass WebAuthn issues
      let assertion;
      const isInIframe = window.self !== window.top;
      const isInReactNativeWebView = !!window.ReactNativeWebView;

      if ((isInIframe && window.parent) || isInReactNativeWebView) {
        // Proxy WebAuthn request to parent window or React Native app
        const requestId = `webauthn-${Date.now()}`;

        // For React Native: send base64url strings directly (not Uint8Array)
        // react-native-passkey expects base64url encoded challenge and credential IDs
        const publicKeyOptionsForProxy = {
          challenge, // Already base64url from server
          allowCredentials: allowCredentials.map(cred => ({
            id: cred.id, // Already base64url from server
            type: cred.type,
            transports: cred.transports,
          })),
          timeout: timeout || 300000,
          userVerification: userVerification || 'required',
          rpId,
        };

        const credentialPromise = new Promise((resolve, reject) => {
          const handleResponse = event => {
            // Parent iframe replies; RN uses ReactNativeWebView (no browser origin check).
            if (!isInReactNativeWebView && !isTrustedWebOrigin(event.origin)) {
              return;
            }
            const data = event.data;
            if (data?.type === 'OMMNIOSIGN_WEBAUTHN_RESPONSE' && data?.requestId === requestId) {
              window.removeEventListener('message', handleResponse);
              if (data.success) {
                resolve(data.credential);
              } else {
                reject(new Error(data.error || 'WebAuthn failed'));
              }
            }
          };

          window.addEventListener('message', handleResponse);

          // Send request to parent or React Native
          const message = {
            type: 'OMMNIOSIGN_WEBAUTHN_REQUEST',
            requestId,
            publicKeyOptions: publicKeyOptionsForProxy,
          };

          if (isInReactNativeWebView) {
            // React Native WebView uses a different postMessage API
            window.ReactNativeWebView.postMessage(JSON.stringify(message));
          } else {
            // Standard iframe postMessage
            window.parent.postMessage(message, getEmbedParentTargetOrigin());
          }

          // Timeout after 5 minutes
          setTimeout(() => {
            window.removeEventListener('message', handleResponse);
            reject(new Error('WebAuthn timeout'));
          }, 300000);
        });

        const proxyCredential = await credentialPromise;
        assertion = prepareProxyAssertion(proxyCredential);
      } else {
        // Direct WebAuthn call (not in iframe or no parent)
        // Browser WebAuthn API requires Uint8Array for challenge and credential IDs
        const publicKeyOptions = {
          challenge: Uint8Array.from(atob(challenge.replace(/-/g, '+').replace(/_/g, '/')), c =>
            c.charCodeAt(0),
          ),
          allowCredentials: allowCredentials.map(cred => ({
            id: Uint8Array.from(atob(cred.id.replace(/-/g, '+').replace(/_/g, '/')), c =>
              c.charCodeAt(0),
            ),
            type: cred.type,
            transports: cred.transports,
          })),
          timeout: timeout || 300000,
          userVerification: userVerification || 'required',
          rpId,
        };

        const credential = await navigator.credentials.get({
          publicKey: publicKeyOptions,
        });

        if (!credential) {
          throw new Error(I18n.t('OmmnioSign.Errors.WebAuthnFailed'));
        }

        // Prepare assertion for server (standard WebAuthn PublicKeyCredential format)
        assertion = {
          id: credential.id,
          rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, ''),
          type: credential.type,
          response: {
            authenticatorData: btoa(
              String.fromCharCode(...new Uint8Array(credential.response.authenticatorData)),
            )
              .replace(/\+/g, '-')
              .replace(/\//g, '_')
              .replace(/=/g, ''),
            clientDataJSON: btoa(
              String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON)),
            )
              .replace(/\+/g, '-')
              .replace(/\//g, '_')
              .replace(/=/g, ''),
            signature: btoa(String.fromCharCode(...new Uint8Array(credential.response.signature)))
              .replace(/\+/g, '-')
              .replace(/\//g, '_')
              .replace(/=/g, ''),
            ...(credential.response.userHandle
              ? {
                  userHandle: btoa(
                    String.fromCharCode(...new Uint8Array(credential.response.userHandle)),
                  )
                    .replace(/\+/g, '-')
                    .replace(/\//g, '_')
                    .replace(/=/g, ''),
                }
              : {}),
          },
        };
      }

      // 4. Verify WebAuthn assertion with OmmnioSign
      // Backend gets employeeId from authenticated session and derives requestId from signerToken
      const verifyResponse = await Http.post('/v1/ommniosign/webauthn/verify', {
        signerToken,
        evidenceId,
        assertion,
      });

      // Http.post already unwraps the response, so verifyResponse = { status: true, data: { valid, evidenceId, message } }
      if (verifyResponse.status && verifyResponse.data?.valid) {
        const authData = {
          credentialId: assertion.id,
          authenticatorData: assertion.response.authenticatorData,
          clientDataJSON: assertion.response.clientDataJSON,
          signature: assertion.response.signature,
          userVerification: true,
        };

        setLoading(false);
        webAuthnInProgressRef.current = false;
        onAuthComplete('webauthn', authData, verifyResponse.data.evidenceId);
      } else {
        throw new Error(
          verifyResponse.data?.message ||
            verifyResponse.error ||
            I18n.t('OmmnioSign.Errors.WebAuthnFailed'),
        );
      }
    } catch (err) {
      setLoading(false);
      webAuthnInProgressRef.current = false;

      if (isPasskeyPromptCancelled(err)) {
        userCancelledRef.current = true;
        forceVerifiedPasskeyRef.current = false;
        setLoading(false);
        webAuthnInProgressRef.current = false;
        if (pendingUpgradeCredentialsRef.current.length > 0) {
          setUpgradeCredentials(pendingUpgradeCredentialsRef.current);
          setUpgradeAvailable(true);
          setVerifiedPasskeyAvailable(true);
          setSmsFallback(false);
          setError(null);
        } else {
          // Verified passkey is on another device — offer to add one here instead of SMS-only.
          setOfferingLocalPasskeyRegistration(true);
          setOfferingRegistration(true);
          setSmsFallback(false);
          setUpgradeAvailable(false);
          setError(null);
        }
      } else {
        setError(normalizeOmmnioSignError(err, I18n.t('OmmnioSign.Errors.WebAuthnFailed')));
      }
    }
  }, [signerToken, onAuthComplete]);

  // Reuse an active OTP signing session on entry (web and app WebView).
  useEffect(() => {
    if (checkingPasskeys) {
      return undefined;
    }

    if (!shouldAutoReuseOtpSigningSession(enrollmentStartedRef.current)) {
      setSessionProbeComplete(true);
      return undefined;
    }

    let cancelled = false;

    const reuseOtpSigningSession = async () => {
      try {
        // Read-only probe: never sends SMS and never mints evidence, so an
        // abandoned signer cannot burn OTP/session capacity just by arriving here.
        const status = await checkSessionStatus({ signerToken }).unwrap();

        if (!cancelled && status?.data?.sessionActive) {
          const signature = extractSignatureFromWidgets(widgets);
          const authResult = await authorizeSession({ signerToken, signature, widgets }).unwrap();

          if (!cancelled && authResult?.status && authResult?.data?.valid) {
            onAuthComplete('otp', { sessionActive: true }, authResult.data.evidenceId);
            return;
          }
        }
      } catch {
        // No active session (or probe failed) — show the normal authentication UI.
      }

      if (!cancelled) {
        setSessionProbeComplete(true);
      }
    };

    reuseOtpSigningSession();

    return () => {
      cancelled = true;
    };
  }, [
    checkingPasskeys,
    checkSessionStatus,
    authorizeSession,
    signerToken,
    widgets,
    onAuthComplete,
  ]);

  // Auto-trigger biometric auth if already configured
  useEffect(() => {
    // Only auto-trigger if:
    // 1. Passkey check is complete
    // 2. Active signing session probe finished (no reusable OTP session)
    // 3. Device supports WebAuthn
    // 4. User has registered passkeys
    // 5. No upgrade needed
    // 6. Not already loading or in progress
    // 7. User hasn't cancelled
    // 8. No error state
    if (
      !emailChannelUsesOtpOnly &&
      !checkingPasskeys &&
      sessionProbeComplete &&
      deviceSupportsWebAuthn &&
      hasPasskeys &&
      !upgradeAvailable &&
      !smsFallback &&
      !loading &&
      !webAuthnInProgressRef.current &&
      !userCancelledRef.current &&
      !offeringLocalPasskeyRegistration &&
      !error
    ) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        handleWebAuthnAuth();
      }, 300);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [
    emailChannelUsesOtpOnly,
    checkingPasskeys,
    sessionProbeComplete,
    deviceSupportsWebAuthn,
    hasPasskeys,
    upgradeAvailable,
    smsFallback,
    offeringLocalPasskeyRegistration,
    loading,
    error,
    handleWebAuthnAuth,
  ]);

  // Extract signature widget data for backend validation (shared by OTP verify and session authorize)
  const extractSignature = useCallback(() => extractSignatureFromWidgets(widgets), [widgets]);

  const handleSendOTP = useCallback(
    async (intent = {}) => {
      try {
        setLoading(true);
        setError(null);

        // Backend gets employeeId from authenticated session and derives requestId from signerToken
        const appHash = getAndroidAppHashFromBridge();
        const result = await requestOTP(
          appHash ? { signerToken, appHash } : { signerToken },
        ).unwrap();

        // Active authentication session: backend skips the SMS and lets us authorize directly
        if (result?.data?.sessionActive) {
          setSessionReusable(true);
          const signature = extractSignature();
          const authResult = await authorizeSession({ signerToken, signature, widgets }).unwrap();

          if (authResult?.status && authResult?.data?.valid) {
            const next = sessionReuseContinueAction(intent);
            if (next === 'registerPasskey') {
              await handleRegisterPasskeyRef.current?.(authResult.data.evidenceId);
              return;
            }

            if (next === 'upgradePasskey') {
              await handleUpgradeCredentialRef.current?.(authResult.data.evidenceId);
              return;
            }

            onAuthComplete('otp', { sessionActive: true }, authResult.data?.evidenceId);
            return;
          }

          throw new Error(authResult?.data?.message || I18n.t('OmmnioSign.Errors.OTPVerifyFailed'));
        }

        setOtpSent(true);
        setLoading(false);

        if (isReactNativeWebView()) {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({ type: 'OMMNIOSIGN_OTP_LISTEN_START' }),
          );
        }
      } catch (err) {
        setLoading(false);
        const reason = err?.data?.data?.reason ?? err?.data?.reason;
        if (reason === 'webauthn_required') {
          setError(I18n.t('OmmnioSign.Errors.WebAuthnRequired'));
          return;
        }
        setError(normalizeOmmnioSignError(err, I18n.t('OmmnioSign.Errors.OTPSendFailed')));
      }
    },
    [requestOTP, signerToken, authorizeSession, extractSignature, widgets, onAuthComplete],
  );

  const handleVerifyOTP = useCallback(
    async (codeOverride = null) => {
      const code = typeof codeOverride === 'string' ? codeOverride : otpCode;
      if (!code || code.length < 4) {
        setError(I18n.t('OmmnioSign.Errors.InvalidOTP'));
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await verifyOTP({
          code,
          signerToken,
        }).unwrap();

        // Backend returns: { status: true, data: { valid: true, evidenceId: "...", message: "..." } }
        if (result.status && result.data?.valid) {
          // If we're in registration flow, proceed to register passkey
          if (registering) {
            await handleRegisterPasskeyRef.current?.(result.data.evidenceId);
            return;
          }

          // If we're in upgrade flow, proceed to upgrade credential
          if (upgradingCredential) {
            await handleUpgradeCredentialRef.current?.(result.data.evidenceId);
            return;
          }

          const authData = {
            code,
          };

          setLoading(false);
          // Pass evidenceId as third parameter for eIDAS compliance
          onAuthComplete('otp', authData, result.data?.evidenceId);
        } else {
          throw new Error(result.data?.message || I18n.t('OmmnioSign.Errors.OTPVerifyFailed'));
        }
      } catch (err) {
        setLoading(false);
        setError(normalizeOmmnioSignError(err, I18n.t('OmmnioSign.Errors.OTPVerifyFailed')));
      }
    },
    [
      verifyOTP,
      otpCode,
      onAuthComplete,
      signerToken,
      registering,
      upgradingCredential,
      // Note: handleRegisterPasskey and handleUpgradeCredential are intentionally omitted
      // to avoid circular dependency (they're defined after this callback)
    ],
  );

  useEffect(() => {
    handleVerifyOTPRef.current = handleVerifyOTP;
  }, [handleVerifyOTP]);

  // Start passkey registration flow
  const handleStartRegistration = useCallback(async () => {
    try {
      enrollmentStartedRef.current = true;
      setRegistering(true);
      setError(null);

      // Step 1: Request OTP for verification (or reuse session, then enroll)
      await handleSendOTP({ forRegistration: true });
    } catch (err) {
      setRegistering(false);
      setError(err.message || I18n.t('OmmnioSign.Errors.OTPSendFailed'));
    }
  }, [handleSendOTP]);

  // Register passkey after OTP verification
  const handleRegisterPasskey = useCallback(
    async otpEvidenceId => {
      try {
        setLoading(true);
        setError(null);

        // Step 1: Request registration challenge from backend with OTP proof
        const challengeResponse = await Http.post('/v1/ommniosign/webauthn/register/begin', {
          signerToken,
          authEvidenceId: otpEvidenceId, // Proof of phone ownership
          context: 'ommniosign_signing',
        });

        if (!challengeResponse.status) {
          throw new Error(challengeResponse.data?.error || 'Failed to get registration challenge');
        }

        const {
          challenge,
          user,
          rp,
          pubKeyCredParams,
          timeout,
          authenticatorSelection,
          attestation,
        } = challengeResponse.data;

        // Step 2: Create credential - proxy to React Native or use browser API
        let credential;
        if (isReactNativeWebView()) {
          // Proxy to React Native - send base64url format directly
          credential = await proxyWebAuthnCreate({
            challenge,
            user,
            rp,
            pubKeyCredParams,
            timeout,
            authenticatorSelection,
            attestation: attestation || 'none',
          });
        } else {
          // Browser WebAuthn API - needs ArrayBuffer conversion
          const browserCredential = await navigator.credentials.create({
            publicKey: preparePublicKeyOptions({
              challenge,
              user,
              rp,
              pubKeyCredParams,
              timeout,
              authenticatorSelection,
              attestation: attestation || 'none',
            }),
          });
          credential = preparePublicKeyCredential(browserCredential);
        }

        // Step 3: Send credential to backend for verification and storage
        const registrationResponse = await Http.post('/v1/ommniosign/webauthn/register/finish', {
          signerToken,
          credential,
          authEvidenceId: otpEvidenceId, // Link registration to OTP verification
        });

        if (!registrationResponse.status) {
          throw new Error(registrationResponse.data?.error || 'Failed to complete registration');
        }

        // Success! Now use WebAuthn to authenticate for signing
        setHasPasskeys(true);
        setAuthMethod('webauthn');
        setRegistering(false);
        setOfferingRegistration(false);
        setOtpSent(false);
        setOtpCode('');

        // Automatically proceed to WebAuthn authentication
        setTimeout(() => {
          handleWebAuthnAuth();
        }, 500);
      } catch (err) {
        setLoading(false);
        setRegistering(false);
        setError(
          err.message ||
            I18n.t('OmmnioSign.Authentication.PasskeyRegistrationFailed') ||
            'Failed to register passkey',
        );
      }
    },
    [signerToken, handleWebAuthnAuth],
  );

  // Handle credential upgrade flow
  const handleStartUpgrade = useCallback(async () => {
    try {
      enrollmentStartedRef.current = true;
      setUpgradingCredential(true);
      setError(null);

      // Step 1: Send OTP for verification (or reuse session, then upgrade)
      await handleSendOTP({ forUpgrade: true });
    } catch (err) {
      setUpgradingCredential(false);
      setError(err.message || I18n.t('OmmnioSign.Errors.OTPSendFailed'));
    }
  }, [handleSendOTP]);

  const handleUpgradeCredential = useCallback(
    async otpEvidenceId => {
      try {
        setLoading(true);
        setError(null);

        // Get the first non-OTP credential to upgrade (or let user choose in the future)
        const credentialToUpgrade = upgradeCredentials[0];

        if (!credentialToUpgrade) {
          throw new Error('No credential to upgrade');
        }

        // Call upgrade endpoint
        const upgradeResponse = await Http.post('/v1/ommniosign/webauthn/upgrade', {
          signerToken,
          credentialId: credentialToUpgrade.id,
          authEvidenceId: otpEvidenceId, // Link upgrade to OTP verification
        });

        if (!upgradeResponse.status) {
          throw new Error(upgradeResponse.data?.error || 'Failed to upgrade passkey');
        }

        // Success! Credential is now OTP-verified
        setUpgradeAvailable(false);
        setUpgradeCredentials([]);
        setUpgradingCredential(false);
        setOtpSent(false);
        setOtpCode('');
        setHasPasskeys(true);

        // Automatically proceed to WebAuthn authentication
        setTimeout(() => {
          handleWebAuthnAuth();
        }, 500);
      } catch (err) {
        setLoading(false);
        setUpgradingCredential(false);
        setError(
          err.message ||
            I18n.t('OmmnioSign.Authentication.PasskeyUpgradeFailed') ||
            'Failed to verify passkey',
        );
      }
    },
    [signerToken, upgradeCredentials, handleWebAuthnAuth],
  );

  useEffect(() => {
    handleRegisterPasskeyRef.current = handleRegisterPasskey;
    handleUpgradeCredentialRef.current = handleUpgradeCredential;
  }, [handleRegisterPasskey, handleUpgradeCredential]);

  return (
    <Box sx={rootSx} className="ommniosign-auth-layout">
      <Box sx={contentSx} className="ommniosign-auth-content">
        <Typography variant="body2" color="textSecondary" paragraph>
          {I18n.t('OmmnioSign.Authentication.Description')}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {emailChannelUsesOtpOnly && !hasPhoneForEmailOtp && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {I18n.t('OmmnioSign.Authentication.EmailSigningPhoneRequired')}
          </Alert>
        )}

        {checkingPasskeys || !sessionProbeComplete ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          !(emailChannelUsesOtpOnly && !hasPhoneForEmailOtp) && (
            <>
              {/* Case 1: Device supports biometrics AND user has passkeys */}
              {deviceSupportsWebAuthn &&
                hasPasskeys &&
                !upgradeAvailable &&
                !smsFallback &&
                !offeringLocalPasskeyRegistration && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    onClick={() => {
                      userCancelledRef.current = false;
                      setError(null);
                      handleWebAuthnAuth();
                    }}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <FingerprintIcon />}
                    sx={{ mt: 2, py: 1.5, mb: 2 }}
                  >
                    {loading
                      ? I18n.t('OmmnioSign.Authentication.Authenticating')
                      : I18n.t('OmmnioSign.Authentication.AuthenticateWithBiometrics')}
                  </Button>
                )}

              {/* Case 1.5: User has passkeys but they need OTP verification - OFFER UPGRADE */}
              {upgradeAvailable && upgradeCredentials.length > 0 && (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      gutterBottom
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      {I18n.t('OmmnioSign.Authentication.VerifyPasskeyTitle')}
                    </Typography>
                    <Typography variant="caption">
                      {I18n.t('OmmnioSign.Authentication.VerifyPasskeyBody')}
                    </Typography>
                  </Alert>

                  {!upgradingCredential ? (
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        onClick={handleStartUpgrade}
                        disabled={loading}
                        startIcon={<FingerprintIcon />}
                        sx={{ py: 1.5, mb: 2 }}
                      >
                        {I18n.t('OmmnioSign.Authentication.VerifyPasskey')}
                      </Button>

                      {verifiedPasskeyAvailable && (
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => {
                            userCancelledRef.current = false;
                            setError(null);
                            forceVerifiedPasskeyRef.current = true;
                            handleWebAuthnAuth();
                          }}
                          disabled={loading}
                          startIcon={<FingerprintIcon />}
                          sx={{ mb: 2 }}
                        >
                          {I18n.t('OmmnioSign.Authentication.AuthenticateWithBiometrics')}
                        </Button>
                      )}

                      {sessionReusable ? (
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => handleSendOTP()}
                          disabled={loading}
                        >
                          {I18n.t('OmmnioSign.Authentication.Continue')}
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => {
                            setUpgradeAvailable(false);
                            setSmsFallback(true);
                            setAuthMethod('otp');
                          }}
                          startIcon={<SmsIcon />}
                        >
                          {I18n.t('OmmnioSign.Authentication.ContinueWithSMS')}
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      {!otpSent ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            p: 3,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <>
                          <Typography variant="body2" paragraph>
                            {I18n.t('OmmnioSign.Authentication.VerifyPhoneForUpgrade')}
                          </Typography>

                          <TextField
                            sx={otpInputSx}
                            fullWidth
                            label={I18n.t('OmmnioSign.Authentication.OTPCode')}
                            value={otpCode}
                            onChange={e => setOtpCode(e.target.value)}
                            disabled={loading}
                            autoFocus
                            slotProps={{ htmlInput: otpInputProps }}
                          />

                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            onClick={handleVerifyOTP}
                            disabled={loading || !otpCode}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                            sx={{ py: 1.5 }}
                          >
                            {loading
                              ? I18n.t('OmmnioSign.Authentication.VerifyingPasskey')
                              : I18n.t('OmmnioSign.Authentication.VerifyAndUpgrade')}
                          </Button>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Case 2: No passkeys yet, or passkey only on another device — OFFER REGISTRATION */}
              {deviceSupportsWebAuthn &&
                offeringRegistration &&
                (!hasPasskeys || offeringLocalPasskeyRegistration) && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        gutterBottom
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        {I18n.t('OmmnioSign.Authentication.SetupBiometricsTitle')}
                      </Typography>
                      <Typography variant="caption">
                        {I18n.t('OmmnioSign.Authentication.SetupBiometricsBody')}
                      </Typography>
                    </Alert>

                    {!registering ? (
                      <Box>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          fullWidth
                          onClick={handleStartRegistration}
                          disabled={loading}
                          startIcon={<FingerprintIcon />}
                          sx={{ py: 1.5, mb: 2 }}
                        >
                          {I18n.t('OmmnioSign.Authentication.SetupBiometrics')}
                        </Button>

                        {sessionReusable ? (
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => handleSendOTP()}
                            disabled={loading}
                          >
                            {I18n.t('OmmnioSign.Authentication.Continue')}
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={() => {
                                setOfferingRegistration(false);
                                setOfferingLocalPasskeyRegistration(false);
                                setSmsFallback(true);
                                setAuthMethod('otp');
                              }}
                              startIcon={<SmsIcon />}
                            >
                              {I18n.t('OmmnioSign.Authentication.ContinueWithSMS')}
                            </Button>
                            {offeringLocalPasskeyRegistration && (
                              <Button
                                variant="text"
                                fullWidth
                                onClick={() => {
                                  userCancelledRef.current = false;
                                  setOfferingRegistration(false);
                                  setOfferingLocalPasskeyRegistration(false);
                                  setError(null);
                                  forceVerifiedPasskeyRef.current = true;
                                  handleWebAuthnAuth();
                                }}
                                disabled={loading}
                                sx={{ mt: 1 }}
                              >
                                {I18n.t('OmmnioSign.Authentication.AuthenticateWithBiometrics')}
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        {!otpSent ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              p: 3,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : (
                          <>
                            <Typography variant="body2" paragraph>
                              {I18n.t('OmmnioSign.Authentication.VerifyPhoneForRegistration')}
                            </Typography>
                            <TextField
                              sx={otpInputSx}
                              fullWidth
                              label={I18n.t('OmmnioSign.Authentication.OTPCode')}
                              value={otpCode}
                              onChange={e => setOtpCode(e.target.value)}
                              placeholder="123456"
                              slotProps={{ htmlInput: otpInputProps }}
                              autoFocus
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              onClick={handleVerifyOTP}
                              disabled={loading || !otpCode}
                              startIcon={loading ? <CircularProgress size={20} /> : null}
                            >
                              {loading
                                ? I18n.t('OmmnioSign.Authentication.RegisteringPasskey')
                                : I18n.t('OmmnioSign.Authentication.VerifyAndRegister')}
                            </Button>
                            <Button
                              variant="text"
                              fullWidth
                              onClick={() => {
                                setRegistering(false);
                                setOtpSent(false);
                                setOtpCode('');
                              }}
                              disabled={loading}
                              sx={{ mt: 1 }}
                            >
                              {I18n.t('Common.Cancel')}
                            </Button>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                )}

              {/* Case 3: Device doesn't support biometrics OR user declined registration - use SMS silently */}
              {((smsFallback && !upgradeAvailable) ||
                !deviceSupportsWebAuthn ||
                (!hasPasskeys && !offeringRegistration)) && (
                <Box>
                  {!otpSent ? (
                    sessionReusable ? (
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => handleSendOTP()}
                        disabled={loading}
                      >
                        {I18n.t('OmmnioSign.Authentication.Continue')}
                      </Button>
                    ) : (
                      <>
                        <Typography variant="body2" paragraph>
                          {I18n.t('OmmnioSign.Authentication.OTPInstructions')}
                        </Typography>

                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={handleSendOTP}
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={20} /> : <SmsIcon />}
                        >
                          {loading
                            ? I18n.t('OmmnioSign.Authentication.SendingOTP')
                            : I18n.t('OmmnioSign.Authentication.SendOTP')}
                        </Button>
                        {smsFallback && deviceSupportsWebAuthn && hasPasskeys && (
                          <Button
                            variant="text"
                            fullWidth
                            onClick={() => {
                              userCancelledRef.current = false;
                              setSmsFallback(false);
                              setError(null);
                              // Mixed accounts would otherwise return to the choice screen.
                              forceVerifiedPasskeyRef.current = true;
                              handleWebAuthnAuth();
                            }}
                            disabled={loading}
                            sx={{ mt: 1 }}
                          >
                            {I18n.t('OmmnioSign.Authentication.AuthenticateWithBiometrics')}
                          </Button>
                        )}
                      </>
                    )
                  ) : (
                    <>
                      <Typography variant="body2" paragraph>
                        {I18n.t('OmmnioSign.Authentication.EnterOTP')}
                      </Typography>
                      <TextField
                        sx={otpInputSx}
                        fullWidth
                        label={I18n.t('OmmnioSign.Authentication.OTPCode')}
                        value={otpCode}
                        onChange={e => setOtpCode(e.target.value)}
                        slotProps={{ htmlInput: otpInputProps }}
                        placeholder="123456"
                        autoFocus
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleVerifyOTP}
                        disabled={loading || !otpCode}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                      >
                        {loading
                          ? I18n.t('OmmnioSign.Authentication.Verifying')
                          : I18n.t('OmmnioSign.Authentication.Verify')}
                      </Button>
                      <Button
                        variant="text"
                        fullWidth
                        onClick={handleSendOTP}
                        disabled={loading}
                        sx={{ mt: 1 }}
                      >
                        {I18n.t('OmmnioSign.Authentication.ResendOTP')}
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </>
          )
        )}
      </Box>
      <Box sx={actionsSx} className="ommniosign-embedded-actions">
        <Button onClick={onBack} disabled={loading || !sessionProbeComplete}>
          {I18n.t('Common.Back')}
        </Button>
      </Box>
    </Box>
  );
};

AuthenticationStep.propTypes = {
  signerToken: string.isRequired,
  widgets: object,
  documentName: string,
  deliveryChannel: string,
  signerPhone: string,
  companyId: string,
  onAuthComplete: func.isRequired,
  onBack: func.isRequired,
};

export default AuthenticationStep;
